import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { TStore } from "../../store/Store"
import {
    resetRaiseTicketForm,
    setDealSowForm,
    setFormModule,
    setMaterialDispatchForm,
    setPaymentDetailsFormValue,
    setPOCFormModule,
    setSitesFormModule,
    setSoloFormModule,
    setStep,
} from "./RaiseTicketFormSlice"
import { BASE_STEPS, DEAL_TEMPLATES, DEAL_TYPE, FORM_STEPS } from "./config/projectFormConstants"
import { getStepsConfig, getStepsForTemplate, STEPS_CONFIG } from "./config/mapping"
import { EFormModule } from "."
import { DropdownObj } from "../../pages/Common/commonTypes"

const useRaiseTicketForm = ({
    module,
    onClose,
    isOpen,
}: {
    module: EFormModule
    onClose: () => void
    isOpen: boolean
}) => {
    const dispatch = useDispatch()
    const {
        step,
        deal_sow_form: { deal_template, deal_type },
    } = useSelector((state: TStore) => state.RaiseTicketForm)
    const [allowedSteps, setAllowedSteps] = useState<Set<number>>(new Set([1]))

    /**
     * Get the steps array based on the deal template
     * @returns {string[]} The steps array
     */
    const stepsArray: string[] = useMemo(() => {
        return getStepsForTemplate(deal_template!.id as DEAL_TEMPLATES, deal_type.title as DEAL_TYPE).map(
            step => step.title
        )
    }, [deal_template, deal_type])

    /**
     * Get the current page based on the deal template and step
     * @returns {FORM_STEPS} The current page
     */
    const currentPage: FORM_STEPS = useMemo(() => {
        return (getStepsConfig(deal_template!.id as DEAL_TEMPLATES, deal_type.title as DEAL_TYPE) || BASE_STEPS)[step - 1]
    }, [step, deal_template, stepsArray])


    const handleClose = () => {
        dispatch(resetRaiseTicketForm())
        onClose()
    }

    const handleStepChange = (active: number) => {
        if (step === stepsArray.length && active > step) {
            handleClose()
            return
        }
        dispatch(setStep(active))
        setAllowedSteps(prev => new Set(prev).add(active))
    }

    /**
     * Handle the next step
     */
    const handleNextStep = () => {
        const nextStep = step + 1
        handleStepChange(nextStep)
        // TODO: Add logic call the get ticket details API
    }

    useEffect(() => {
        dispatch(setFormModule(module))
        return () => {
            dispatch(resetRaiseTicketForm())
        }
    }, [dispatch, isOpen])

    useEffect(() => {
        dispatch(setStep(1))
        dispatch(
            setDealSowForm({
                value: {} as DropdownObj,
                key: "deal_type",
            })
        )
        dispatch(
            setSitesFormModule({
                siteType: {} as DropdownObj,
                siteName: "",
                siteAddress: "",
                pincode: "",
                state: null,
                city: null,
                latitude: "",
                longitude: "",
                country:{} as DropdownObj,
                search_site_name: "",
                search_pincode: "",
                siteId: null,
                selectedState: [],
                selectedCity: [],
                selectedCountry:{} as DropdownObj,
                addedBy: [],
            })
        )
        dispatch(
            setSoloFormModule({
                visit_by_date: "",
                visit_by_time: {} as DropdownObj,
                spot_image: [],
                load_available: "",
                electricity_bill: [],
                foundation_type: {} as DropdownObj,
                connection_type: {} as DropdownObj,
                business_poc: {} as DropdownObj,
                survey_completion_date: "",
                demand_load: "",
                sanctioned_load: "",
                parking_slot: "",
                remarks: "",
                project_name: "",
                chargers: [
                    {
                        id: Date.now(),
                        type: null,
                        quantity: "",
                        rating: "",
                    },
                ],
            })
        )
        dispatch(
            setPOCFormModule({
                name: "",
                email: "",
                contact_number: "",
            })
        )
        dispatch(
            setMaterialDispatchForm({
                project_name: "",
                visit_by_date: "",
                visit_by_time: {} as DropdownObj,
                source_site_id: {} as DropdownObj,
                source_address: "",
                source_pincode: "",
                source_state: {} as DropdownObj,
                source_city: {} as DropdownObj,
                source_poc: {
                    name: "",
                    phone: "",
                    email: "",
                },
                destination_poc: {
                    name: "",
                    phone: "",
                    email: "",
                },
                materials: [],
                file: "",
                source_latitude: "",
                source_longitude: "",
            })
        )
    }, [isOpen])

    return {
        states: {
            step,
            deal_template,
            currentPage,
            allowedSteps,
            stepsArray,
        },
        functions: {
            handleStepChange,
            handleNextStep,
            setStep,
            handleClose
        },
    }
}

export default useRaiseTicketForm
